import React, { useState } from 'react';
import { services } from '../mock';
import { Check, Info, X } from 'lucide-react';
import BeatCatalog from './BeatCatalog';
import MixMasterModal from './MixMasterModal';
import VideoDepositModal from './VideoDepositModal';

const Services = () => {
  const [isBeatCatalogOpen, setIsBeatCatalogOpen] = useState(false);
  const [isMixMasterOpen, setIsMixMasterOpen] = useState(false);
  const [isVideoDepositOpen, setIsVideoDepositOpen] = useState(false);
  const [showLicenseInfo, setShowLicenseInfo] = useState(null);

  const licenseInfo = {
    lease: {
      title: 'Lease License - $40',
      features: [
        '✓ Create 1 commercial song',
        '✓ 1-year term',
        '✓ Distribute worldwide (Spotify, Apple Music, etc.)',
        '✓ 1 music video allowed',
        '✓ Live performances',
        '✗ Beat can be sold to others',
        '✗ No ownership rights',
        '⚠️ All royalties remain with producer'
      ]
    },
    unlimited: {
      title: 'Unlimited License - $80',
      features: [
        '✓ Unlimited commercial songs',
        '✓ No expiration',
        '✓ Unlimited distribution & streams',
        '✓ Unlimited music videos',
        '✓ Radio & promotional use',
        '✗ Beat can be sold to others',
        '✗ No ownership rights',
        '⚠️ All royalties remain with producer'
      ]
    },
    exclusive: {
      title: 'Exclusive Rights - Custom Price',
      features: [
        '✓ Full exclusive ownership',
        '✓ Beat removed from sale',
        '✓ Stems/Trackouts included',
        '✓ Unlimited songs & videos',
        '✓ Film/TV/Commercial use',
        '✓ Worldwide distribution',
        '✓ 50/50 royalty split with producer',
        '⚠️ Requires approval (24-48 hours)'
      ]
    }
  };

  return (
    <>
      <BeatCatalog isOpen={isBeatCatalogOpen} onClose={() => setIsBeatCatalogOpen(false)} />
      <MixMasterModal isOpen={isMixMasterOpen} onClose={() => setIsMixMasterOpen(false)} />
      <VideoDepositModal isOpen={isVideoDepositOpen} onClose={() => setIsVideoDepositOpen(false)} />
      <section id="services" className="py-20 bg-gradient-to-b from-black to-purple-950/20">
        <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Our <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Services</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Professional recording, mixing, and video production services tailored to your creative needs
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={service.id}
              className="group relative bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-2xl p-8 hover:border-purple-500/50 transition-all hover:scale-105"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/0 via-blue-600/0 to-purple-600/0 group-hover:from-purple-600/10 group-hover:via-blue-600/10 group-hover:to-purple-600/10 rounded-2xl transition-all"></div>

              <div className="relative z-10">
                {/* Title */}
                <h3 className="text-2xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-gray-400 mb-6">{service.description}</p>

                {/* Pricing */}
                <div className="mb-6 space-y-3">
                  {service.pricing.map((price, idx) => (
                    <div key={idx} className="bg-black/40 rounded-lg p-4 border border-purple-500/10">
                      <div className="flex justify-between items-center mb-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-300 text-sm">{price.option}</span>
                          {price.info && (
                            <button
                              onClick={() => setShowLicenseInfo(price.info)}
                              className="text-purple-400 hover:text-purple-300 transition-colors"
                            >
                              <Info size={16} />
                            </button>
                          )}
                        </div>
                        {price.note && (
                          <span className="text-xs bg-purple-600/30 text-purple-300 px-2 py-1 rounded-full">
                            {price.note}
                          </span>
                        )}
                      </div>
                      <div className="text-2xl font-bold text-white">{price.price}</div>
                    </div>
                  ))}
                </div>

                {/* Features */}
                <div className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <Check size={16} className="text-purple-400 flex-shrink-0" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                {service.id === 2 ? (
                  <button
                    onClick={() => setIsMixMasterOpen(true)}
                    className="mt-6 block w-full text-center px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all"
                  >
                    Book Now
                  </button>
                ) : service.id === 3 ? (
                  <button
                    onClick={() => setIsBeatCatalogOpen(true)}
                    className="mt-6 block w-full text-center px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all"
                  >
                    Purchase
                  </button>
                ) : service.id === 4 ? (
                  <button
                    onClick={() => setIsVideoDepositOpen(true)}
                    className="mt-6 block w-full text-center px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all"
                  >
                    Book Now
                  </button>
                ) : (
                  <a
                    href="#booking"
                    className="mt-6 block w-full text-center px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all"
                  >
                    {service.id === 5 ? "Add-On" : "Book Now"}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Studio Policy */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center space-x-2 bg-purple-900/20 backdrop-blur-sm border border-purple-500/30 rounded-full px-6 py-3">
            <span className="text-green-400 text-2xl">✓</span>
            <span className="text-gray-300">Smoking Allowed</span>
          </div>
        </div>
      </div>
    </section>

      {/* License Info Modal */}
      {showLicenseInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setShowLicenseInfo(null)}>
          <div className="relative w-full max-w-md bg-gradient-to-br from-purple-900/40 to-blue-900/40 backdrop-blur-xl border border-purple-500/30 rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-purple-500/30">
              <h3 className="text-xl font-bold text-white">{licenseInfo[showLicenseInfo].title}</h3>
              <button
                onClick={() => setShowLicenseInfo(null)}
                className="w-8 h-8 rounded-full bg-purple-900/30 hover:bg-purple-900/50 flex items-center justify-center transition-colors"
              >
                <X className="text-white" size={18} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-2">
                {licenseInfo[showLicenseInfo].features.map((feature, index) => (
                  <div
                    key={index}
                    className={`text-sm py-2 px-3 rounded-lg ${
                      feature.startsWith('✓')
                        ? 'bg-green-900/20 text-green-300'
                        : feature.startsWith('✗')
                        ? 'bg-red-900/20 text-red-300'
                        : 'bg-yellow-900/20 text-yellow-300'
                    }`}
                  >
                    {feature}
                  </div>
                ))}
              </div>

              <button
                onClick={() => setShowLicenseInfo(null)}
                className="mt-6 w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Services;